import { CommandExecutionContext, SelfContainedCommand } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { createFileLogger } from '../../../utils/logger';
import { googleDriveAdapter } from '../../../services/integrations/adapters/GoogleDriveAdapter';
import { resolveGoogleDriveIntegration } from '../../../services/integrations/utils/integrationResolver';

const log = createFileLogger('GOOGLE_DOC_READ');

export const googleDocReadCommand: SelfContainedCommand = {
  id: 'google_doc_read',
  description: `Read content from a Google Doc

Returns the document content as structured text with paragraphs and formatting information.

**Parameters:**
- documentId: Google Doc ID (from URL: docs.google.com/document/d/{documentId}/edit)
- integrationId (optional): Integration ID (auto-detected if omitted)

**Example URLs:**
- https://docs.google.com/document/d/1ABC...xyz/edit → documentId is "1ABC...xyz"

**Output:**
Returns document title, full text content, and structured paragraphs with styles.`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'documentId',
        type: 'string',
        description: 'Google Doc ID from Drive file URL (e.g., "1ABC...xyz" from docs.google.com/document/d/1ABC...xyz/edit)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Document ID cannot be empty';
          return null;
        }
      },
      {
        name: 'integrationId',
        type: 'string',
        description: 'Integration ID (auto-detected if omitted)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { documentId: '1ABC123xyz' },
        expectedOutput: createSuccessResult('Successfully read Google Doc', {
          title: 'My Document',
          content_text: 'Document content...',
          paragraphs: []
        }),
        description: 'Read a Google Doc by ID',
        context: 'When you need to read Google Doc content'
      }
    ],
    bad: [
      {
        input: { documentId: '' },
        error: 'Document ID cannot be empty',
        why: 'Document ID is required',
        fix: 'Provide a valid Google Doc ID'
      }
    ]
  },
  
  performance: {
    expectedDuration: 2000,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      let { documentId, integrationId } = params;
      
      if (!documentId) {
        return createErrorResult('Missing document ID', 'documentId parameter is required', 'validation');
      }
      
      log.log('[google_doc_read] Reading Google Doc:', { documentId, hasIntegrationId: !!integrationId });
      
      // Check if documentId is an llmId (entity reference) and resolve to actual Drive ID
      if (documentId.includes('_')) {
        log.log('[google_doc_read] 🔍 Detected llmId, resolving to driveFileId:', documentId);
        try {
          const useEntityStore = (await import('../../../stores/entityStore')).default;
          const allEntities = useEntityStore.getState().entities;
          log.log('[google_doc_read] 🔍 EntityStore has', allEntities.length, 'entities');
          
          const entity = allEntities.find((e: any) => e.llmId === documentId);
          log.log('[google_doc_read] 🔍 Found entity:', { 
            found: !!entity, 
            hasLlmId: entity?.llmId,
            hasSkeleton: !!entity?.skeleton,
            hasDriveFileId: !!entity?.skeleton?.driveFileId,
            driveFileId: entity?.skeleton?.driveFileId,
            entityType: entity?.entityType
          });
          
          if (entity && entity.skeleton?.driveFileId) {
            log.log('[google_doc_read] ✅ Resolved llmId to driveFileId:', { llmId: documentId, driveFileId: entity.skeleton.driveFileId });
            documentId = entity.skeleton.driveFileId;
          } else {
            log.warn('[google_doc_read] ❌ Could not resolve llmId to driveFileId:', { 
              documentId, 
              hasEntity: !!entity,
              hasSkeleton: !!entity?.skeleton,
              skeletonKeys: entity?.skeleton ? Object.keys(entity.skeleton) : []
            });
          }
        } catch (error) {
          log.error('[google_doc_read] ❌ Error resolving llmId:', error);
        }
      } else {
        log.log('[google_doc_read] 💡 documentId does not look like llmId (no underscore):', documentId);
      }
      
      // Resolve integration ID if not provided
      let resolvedIntegrationId = integrationId;
      if (!resolvedIntegrationId) {
        try {
          resolvedIntegrationId = await resolveGoogleDriveIntegration(context.userId);
          log.log('[google_doc_read] Auto-resolved integration ID:', resolvedIntegrationId);
        } catch (error) {
          return createErrorResult(
            'No Google Drive integration found',
            error instanceof Error ? error.message : 'Please connect Google Drive in Settings → Integrations',
            'validation'
          );
        }
      }
      
      // Read document via adapter
      const result = await googleDriveAdapter.readGoogleDoc(resolvedIntegrationId, documentId);
      
      log.log('[google_doc_read] Successfully read document:', result.title);
      
      // Calculate document length for accurate insertions
      const contentLength = result.content_text?.length || 0;
      const endIndex = result.end_index || contentLength + 1; // +1 for document start index
      
      return createSuccessResult(
        `Successfully read Google Doc: "${result.title}" (${contentLength} characters, end index: ${endIndex})`,
        {
          document_id: result.document_id,
          title: result.title,
          content_text: result.content_text,
          paragraphs: result.paragraphs,
          paragraph_count: result.paragraphs?.length || 0,
          content_length: contentLength,
          end_index: endIndex,
          insert_at_end_index: endIndex // Clear instruction for appending
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'google_doc_read',
            hint: `To append content, use index ${endIndex}`
          } as any
        }
      );
      
    } catch (error) {
      log.error('[google_doc_read] Error reading Google Doc:', error);
      return createErrorResult(
        'Failed to read Google Doc',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

